import { CSSProperties } from "react";

// Subsection title (Header, Social Media, About/Bio, Markets, Testimonials, …).
// Figma iOS/H4: 16px bold, primary text (MAX-3868). The outer "Agent Bio Page" title
// (18px) is owned by the hosting Website tab. 16px title→content gap is the Figma default;
// sections with a description below the title override to 24px via sectionTitleWithDescription.
export const sectionTitle: CSSProperties = {
	fontWeight: 700,
	fontSize: 16,
	lineHeight: "24px",
	letterSpacing: 0,
	color: "#1C1B1F",
	marginBottom: 16,
};

// Title override for sections that place a description directly below the title (24px gap, Figma).
export const sectionTitleWithDescription: CSSProperties = {
	...sectionTitle,
	marginBottom: 24,
};

// Field label (Typography/Web/Fields/Title - Figma spec)
export const fieldLabel: CSSProperties = {
	fontWeight: 400,
	fontSize: 14,
	lineHeight: "20px",
	letterSpacing: 0,
	color: "#5F6368",
	marginBottom: 8,
};

// Description/helper text — primary text color everywhere (MAX-3866/MAX-3868).
// 24px below to the following content, per Figma.
export const descriptionText: CSSProperties = {
	fontSize: 16,
	fontWeight: 400,
	lineHeight: "24px",
	color: "#1C1B1F",
	marginBottom: 24,
};

// Upload box (empty state)
export const uploadBox: CSSProperties = {
	border: "2px dashed #ccc",
	borderRadius: 4,
	padding: 48,
	textAlign: "center",
	backgroundColor: "#fafafa",
	position: "relative",
};

// Upload box (with image)
export const uploadBoxWithImage: CSSProperties = {
	border: "2px dashed #ccc",
	borderRadius: 4,
	padding: 24,
	textAlign: "center",
	backgroundColor: "#fafafa",
	marginBottom: 8,
	position: "relative",
};

// Remove button (X button on image)
export const removeButton: CSSProperties = {
	position: "absolute",
	top: 8,
	right: 8,
	backgroundColor: "white",
	boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
};

// Image preview
export const imagePreview: CSSProperties = {
	maxWidth: "100%",
	display: "block",
	margin: "0 auto",
	objectFit: "contain",
};

// Hidden upload button container
export const hiddenUploadButton: CSSProperties = {
	position: "absolute",
	opacity: 0,
	pointerEvents: "none" as const,
	width: 0,
	height: 0,
};

// Rich text editor wrapper
export const richTextEditor: CSSProperties = {
	border: "none",
	padding: 0,
	margin: 0,
	width: "100%",
};

// Required asterisk
export const requiredAsterisk: CSSProperties = {
	color: "#d32f2f",
};

// Hero image preview
export const heroImagePreview: CSSProperties = {
	maxWidth: "100%",
	maxHeight: 200,
	display: "block",
	margin: "0 auto",
	objectFit: "contain",
};

// Logo image preview
export const logoImagePreview: CSSProperties = {
	maxWidth: "100%",
	maxHeight: 100,
	display: "block",
	margin: "0 auto",
	objectFit: "contain",
};

// Upload icon
export const uploadIcon: CSSProperties = {
	marginBottom: 8,
};

// Upload link wrapper
export const uploadLinkWrapper: CSSProperties = {
	cursor: "pointer",
};

// Upload link disabled
export const uploadLinkDisabled: CSSProperties = {
	cursor: "not-allowed",
};

// Caption text
export const captionText: CSSProperties = {
	display: "block",
	marginTop: 4,
};

// Error caption
export const errorCaption: CSSProperties = {
	display: "block",
	marginTop: 4,
};

// Modal field label
export const modalFieldLabel: CSSProperties = {
	marginBottom: 8,
	fontWeight: 600,
};

// Modal field spacing
export const modalFieldSpacing: CSSProperties = {
	marginBottom: 24,
};

// Dialog actions padding
export const dialogActions: CSSProperties = {
	padding: 16,
};

// Upload button (Cloudinary)
export const uploadButton: CSSProperties = {
	textTransform: "none",
	padding: 0,
	minWidth: "auto",
	background: "none",
	boxShadow: "none",
};

// Upload button - enabled
export const uploadButtonEnabled: CSSProperties = {
	...uploadButton,
	color: "#1976d2",
};

// Upload button - tertiary (teal) per refresh design (MAX-3868). Used as a text button.
export const uploadButtonTertiary: CSSProperties = {
	...uploadButton,
	color: "#36747B",
	fontSize: 16,
	fontWeight: 600,
	lineHeight: "20px",
};

// Upload button - disabled
export const uploadButtonDisabled: CSSProperties = {
	...uploadButton,
	color: "rgba(0, 0, 0, 0.38)",
};

// Upload text spacing
export const uploadTextSpacing: CSSProperties = {
	marginLeft: 4,
};

// Markets chip wrapper
export const marketsChipWrapper: CSSProperties = {
	gap: "9px 10px",
};

// Testimonials table (Figma: two columns, header row, card border, rounded corners).
export const testimonialsTable: CSSProperties = {
	width: "100%",
	borderCollapse: "separate",
	borderSpacing: 0,
	tableLayout: "fixed",
};

export const testimonialsHeaderCell: CSSProperties = {
	backgroundColor: "#F8F8F8",
	borderBottom: "1px solid #DDE7E8",
	borderTop: "1px solid #DDE7E8",
	padding: 16,
	textAlign: "left",
	fontSize: 16,
	fontWeight: 400,
	lineHeight: "24px",
	color: "#5F6368",
};

export const testimonialsBodyCell: CSSProperties = {
	backgroundColor: "#FFFFFF",
	borderBottom: "1px solid #DDE7E8",
	padding: 16,
	verticalAlign: "top",
	fontSize: 16,
	fontWeight: 400,
	lineHeight: "24px",
	color: "#1C1B1F",
};

// Empty state lives in the Testimonial column only (left-aligned, italic per Figma);
// the Client column gets its own blank cell so the two-column structure is preserved.
export const testimonialsEmptyCell: CSSProperties = {
	...testimonialsBodyCell,
	color: "#5F6368",
	fontStyle: "italic",
};

export const testimonialActions: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 16,
	flexShrink: 0,
};

export const testimonialActionIcon: CSSProperties = {
	cursor: "pointer",
	color: "#5F6368",
	display: "inline-flex",
};

export const clientCellContent: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 8,
};
